<!-- 都道府県、市区町村、電話番号、メールアドレス、コメント入力スライド -->
<script setup lang="ts">
import { ref, toRef, computed } from 'vue';
import type Inquiry from '@/javascripts/utils/inquiry';
import type { InquiryValidation } from '@/javascripts/composables/inquiry/validation';
import { getPrefectures } from '@/javascripts/utils/inquiry_form_options';
import useCity from '@/javascripts/composables/inquiry/city';
import TermOfServiceModal from '@/javascripts/common/kiyaku.vue'
import PrivacyPolicyModal from '@/javascripts/common/privacy_policy_modal.vue'
import LoadingAnimation from '@/javascripts/common/loading_animation.vue'

/** プロパティ */
const props = defineProps<{
  /** 問い合わせオブジェクト */
  inquiry: Inquiry,
  /** 問い合わせバリデーション */
  v$: InquiryValidation
}>();

/** カスタムイベント */
defineEmits(['back']);

/** 都道府県の選択肢リスト */
const prefectures = getPrefectures()

/** inquiry.prefectureCdをref化 */
const prefectureCd = toRef(props.inquiry, 'prefectureCd');

const {
  /** 市区町村の選択肢リスト */
  cities,
  /** 市区町村取得エラー */
  error
} = useCity(prefectureCd);

/** 利用規約モーダルコンポーネント */
const termOfServiceModal = ref<InstanceType<typeof TermOfServiceModal> | null>();
/** 個人情報保護方針モーダルコンポーネント */
const privacyPolicyModal = ref<InstanceType<typeof PrivacyPolicyModal> | null>();
/** ローディングアニメーションコンポーネント */
const loadingAnimation = ref<InstanceType<typeof LoadingAnimation> | null>();

/** 利用規約モーダルの表示 */
const openTermOfServiceModal = () => {
  termOfServiceModal.value?.openModal();
}

/** 利用規約モーダルの表示 */
const openPrivacyPolicyModal = () => {
  privacyPolicyModal.value?.openModal();
}

/** 都道府県と電話番号のバリデーション結果 */
const invalid = computed(() => 
  props.v$.prefectureCd.$invalid ||
  props.v$.phone.$invalid
);

/** 問い合わせデータを送信 */
const post = () => {
  loadingAnimation.value?.show();
  return props.inquiry.post();
}
</script>

<template>
  <div>
    <TermOfServiceModal ref="termOfServiceModal" />
    <PrivacyPolicyModal ref="privacyPolicyModal" />
    <LoadingAnimation ref="loadingAnimation" />
    <div class="inquiry-form-post">
      <div class="inquiry-form-item">
        <div class="inquiry-form-item__heading">
          <p class="inquiry-form-heading">
            <span class="inquiry-form-icon__hissu">必須</span>
            <span>都道府県</span>
          </p>
        </div>
        <div class="inquiry-form-item__contact">
          <span>
            <div class="inquiry-form-select">
              <select
                class="m3p-sp-select m3p-sp-mb15"
                v-model="inquiry.prefectureCd">
                <option disabled value="null">都道府県を選択</option>
                <option v-if="error" disabled>{{ error }}</option>
                <option
                  v-for="prefecture in prefectures"
                  :key="prefecture.cd"
                  :value="prefecture.cd">
                  {{ prefecture.text }}
                </option>
              </select>
            </div>
          </span>
          <p v-for="error in props.v$.prefectureCd.$errors" class="inquiry-form-error">
            {{ error.$message }}
          </p>
        </div>
      </div>

      <div class="inquiry-form-item">
        <div class="inquiry-form-item__heading">
          <p class="inquiry-form-heading">
            <span class="inquiry-form-icon__nini">任意</span>
            <span>市区町村</span>
          </p>
        </div>
        <div class="inquiry-form-item__contact">
          <span>
            <div class="inquiry-form-select">
              <select
                class="m3p-sp-select m3p-sp-mb15"
                v-model="inquiry.cityCd">
                <option disabled value="null">市区町村を選択</option>
                <option
                  v-for="city in cities"
                  :key="city.cd"
                  :value="city.cd">
                  {{ city.text }}
                </option>
              </select>
            </div>
          </span>
        </div>
      </div>

      <div class="inquiry-form-item">
        <div class="inquiry-form-item__heading">
          <p class="inquiry-form-heading">
            <span class="inquiry-form-icon__hissu">必須</span>
            <span>電話番号</span>
          </p>
        </div>
        <div class="inquiry-form-item__contact">
          <span>
            <span>
              <input
                class="inquiry-form-textbox"
                type="tel"
                placeholder="例：09011111111"
                v-model="inquiry.phone">
            </span>
          </span>
          <p v-for="error in props.v$.phone.$errors" class="inquiry-form-error">
            {{ error.$message }}
          </p>
        </div>
      </div>

      <div class="inquiry-form-item">
        <div class="inquiry-form-item__heading">
          <p class="inquiry-form-heading">
            <span class="inquiry-form-icon__nini">任意</span>
            <span>メールアドレス</span>
          </p>
        </div>
        <div class="inquiry-form-item__contact">
          <span>
            <span>
              <input
                class="inquiry-form-textbox"
                type="email"
                placeholder="例：abc@example.com"
                v-model="inquiry.email">
            </span>
          </span>
          <p v-for="error in v$.email.$errors" class="inquiry-form-error">
            {{ error.$message }}
          </p>
        </div>
      </div>

      <div class="inquiry-form-item">
        <div class="inquiry-form-item__heading">
          <p class="inquiry-form-heading">
            <span class="inquiry-form-icon__nini">任意</span>
            <span>コメント</span>
          </p>
        </div>
        <div class="inquiry-form-item__contact">
          <span>
            <span>
              <textarea
                class="inquiry-form-textbox"
                type="text"
                placeholder="400文字以内でご希望・ご質問内容などご記入ください"
                rows="5"
                v-model="inquiry.comment" />
            </span>
          </span>
          <p v-for="error in v$.comment.$errors" class="inquiry-form-error">
            {{ error.$message }}
          </p>
        </div>
      </div>
    </div>
    <p class="submitbtn_before_text">
      <a class="open_modal_link" @click="openTermOfServiceModal()">利用規約</a>、
      <a class="open_modal_link" @click="openPrivacyPolicyModal()">個人情報保護方針</a>に同意し、
    </p>
    <div class="form-btnarea">
      <div class="form-btn__back">
        <a @click.once="$emit('back')">
          <i class="fa-solid fa-chevron-left"></i>戻る
        </a>
      </div>
      <button
        type="button"
        :disabled="invalid"
        class="orange-btn form-btn"
        @click.once="post()">
        無料で登録する<i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>
